import { Alert, Loader, Radio, Stack, Text } from "@mantine/core";
import { Link, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { isApiError } from "../../api/errors";
import {
  useApproveOauthRequest,
  useDenyOauthRequest,
  useGetOauthRequest,
} from "../../api/generated/endpoints/identity/identity";
import { useListMyTeams } from "../../api/generated/endpoints/teams/teams";
import type { MyTeam, OAuthRedirect, OAuthRequestView, TokenScope } from "../../api/generated/model";
import { leaveFor } from "../../platform/browser";
import { ErrorAlert } from "../../platform/ErrorAlert";
import {
  AuthNote,
  AuthPrimaryButton,
  AuthSecondaryButton,
  AuthSelect,
  AuthShell,
} from "../../ui/AuthShell";
import { ACCESS, CONSENT } from "./words";

/**
 * An MCP client's consent (spec platform/mcp-oauth, PR 4), where `/api/oauth/authorize` sends the
 * browser: the app, proven only by what the server can prove (its name, its host and where it
 * sends the browser back, decision 10), the team it may use and how much it may do. Allow and
 * Cancel each answer once and send the browser back to the app.
 */
export function ConsentScreen() {
  const { request } = useSearch({ from: "/oauth/consent" });
  if (request === undefined) return <Ended />;
  // A new request in this tab is a new answer of its own.
  return <Consent key={request} requestId={request} />;
}

/** A request past its 10 minutes, answered, or of another browser: the server says 404 to all. */
function Ended() {
  return (
    <AuthShell title={CONSENT.ended}>
      <Text size="sm">{CONSENT.endedText}</Text>
    </AuthShell>
  );
}

function Consent({ requestId }: { requestId: string }) {
  const asked = useGetOauthRequest(requestId);
  const teams = useListMyTeams();
  if (asked.isError) {
    if (isApiError(asked.error, "NOT_FOUND")) return <Ended />;
    return <Failed error={asked.error} retry={() => void asked.refetch()} />;
  }
  if (teams.isError) return <Failed error={teams.error} retry={() => void teams.refetch()} />;
  if (asked.isPending || teams.isPending) {
    return (
      <AuthShell title={CONSENT.reading}>
        <Loader size="sm" aria-label="Reading the request" />
      </AuthShell>
    );
  }
  return <Answer requestId={requestId} asked={asked.data} teams={teams.data} />;
}

function Failed({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <AuthShell title={CONSENT.reading}>
      <ErrorAlert error={error} />
      <AuthPrimaryButton onClick={retry}>Try again</AuthPrimaryButton>
    </AuthShell>
  );
}

function Answer(props: { requestId: string; asked: OAuthRequestView; teams: MyTeam[] }) {
  const { requestId, asked, teams } = props;
  // The first team, as `/` opens it; `write` when the app asked for it, which only then is offered.
  const [teamId, setTeamId] = useState<string | undefined>(teams[0]?.id);
  const [access, setAccess] = useState<TokenScope>(asked.access);
  // Answered, the browser goes back to the app at once: the page waits, its buttons spent.
  const back = { mutation: { onSuccess: (sent: OAuthRedirect) => leaveFor(sent.redirect_to) } };
  const approve = useApproveOauthRequest(back);
  const deny = useDenyOauthRequest(back);
  const allowing = approve.isPending || approve.isSuccess;
  const cancelling = deny.isPending || deny.isSuccess;
  const error = approve.error ?? deny.error;
  if (isApiError(error, "NOT_FOUND")) return <Ended />;
  return (
    <AuthShell title={CONSENT.title(asked.client_name)}>
      <Stack gap={8}>
        <Text size="sm">{CONSENT.asks(asked.client_name, asked.client_host)}</Text>
        <Text size="sm" c="var(--cai-color-text-muted)">
          {CONSENT.back(asked.redirect_host)}
        </Text>
      </Stack>
      {asked.loopback_only ? (
        <Alert color="yellow" variant="light">
          <Text size="sm">{CONSENT.loopback(asked.client_name, asked.redirect_host)}</Text>
        </Alert>
      ) : null}
      {teams.length === 0 ? (
        <Stack gap={12}>
          <Text size="sm">{CONSENT.noTeam}</Text>
          <AuthSecondaryButton component={Link} to="/teams/new">
            {CONSENT.makeTeam}
          </AuthSecondaryButton>
        </Stack>
      ) : (
        <>
          <AuthSelect
            label={CONSENT.team}
            data={teams.map((team) => ({ value: team.id, label: team.name }))}
            value={teamId}
            onChange={(event) => setTeamId(event.currentTarget.value)}
            disabled={allowing || cancelling}
          />
          <AccessChoice asked={asked.access} value={access} onChange={setAccess} />
        </>
      )}
      {error === null ? null : <ErrorAlert error={error} />}
      <Stack gap={8}>
        {teamId === undefined ? null : (
          <AuthPrimaryButton
            loading={allowing}
            disabled={cancelling}
            onClick={() => approve.mutate({ teamId, requestId, data: { access } })}
          >
            {CONSENT.allow}
          </AuthPrimaryButton>
        )}
        <AuthSecondaryButton
          loading={cancelling}
          disabled={allowing}
          onClick={() => deny.mutate({ requestId })}
        >
          {CONSENT.cancel}
        </AuthSecondaryButton>
      </Stack>
      <AuthNote>{CONSENT.note}</AuthNote>
    </AuthShell>
  );
}

/** Read and write, or read only; an app that asked to read only is never offered more. */
function AccessChoice(props: {
  asked: TokenScope;
  value: TokenScope;
  onChange: (access: TokenScope) => void;
}) {
  const { asked, value, onChange } = props;
  if (asked === "read") {
    return (
      <Stack gap={4}>
        <Text size="sm" fw={600}>
          {`${CONSENT.access}: ${ACCESS.read.label}`}
        </Text>
        <Text size="xs" c="var(--cai-color-text-muted)">
          {ACCESS.read.hint}
        </Text>
      </Stack>
    );
  }
  return (
    <Radio.Group
      label={CONSENT.access}
      value={value}
      onChange={(next) => onChange(next === "read" ? "read" : "write")}
    >
      <Stack gap={10} mt={8}>
        <Radio value="write" label={ACCESS.write.label} description={ACCESS.write.hint} />
        <Radio value="read" label={ACCESS.read.label} description={ACCESS.read.hint} />
      </Stack>
    </Radio.Group>
  );
}
